import {Event} from "../../types/Event";
import {CardSkeleton} from "./CardSkeleton";
import React from "react";
import {
  EntityType,
  type SelectedItem,
  SelectedItemsData
} from "../EntitySelector/EntitySelector.types";

export const loadingSkeleton = Array.from({length: 15}).map((_, index) => (
  <CardSkeleton key={index} />
))

// Determine if an item is selected
export function isSelected<K extends EntityType>(item: SelectedItem<K>, entityType: K, selectedItems: SelectedItemsData) {
  return selectedItems[entityType].some(selectedItem => selectedItem.id === item.id);
}

// Get the site name for an event based on its location
export function getEventCardFooter(event: Event, isMultiSite: boolean = false) {
  // Always show the location name if the event location is a 'place'
  if (event.location.type === 'place') return event.location.name;

  // If the site is not multi-site don't show anything
  if (!isMultiSite) return null;

  // Determine the footer text based on the location type
  switch (event.location.type) {
    case "exhibit": {
      return event.location.site.name;
    }
    case "facility": {
      return event.location.site.name;
    }
    case "zone": {
      return event.location.site.name;
    }
    default: {
      const _exhaustiveCheck: never = event.location;
      throw new Error(`Unhandled location type: "${JSON.stringify(_exhaustiveCheck)}"`);
    }
  }
}

export type EntityItem = {
  [K in EntityType]: {
    item: SelectedItem<K>
    type: K
  }
}[EntityType]

type EntityEntry = {
  [K in EntityType]: [K, SelectedItemsData[K]]
}[EntityType]

// Get the first entity which has selected items
export function getFirstEntityWithItems(items: SelectedItemsData): EntityItem | undefined {
  const entity = (Object.entries(items) as EntityEntry[]).find(([, item]) => !!item.length);

  if (!entity) return undefined;

  // Extract the type and item
  const [type, selectedItems] = entity;
  const item = selectedItems[0];

  if (!item) return undefined;

  return { type, item } as EntityItem;
}
